import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const pageClassName = "w-full md:w-[90%] mx-auto px-4 md:px-0";

export function ClientOverviewLoadingState() {
  return (
    <div className={`${pageClassName} mb-10`}>
      <div className="client-overview-primary-grid grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="w-full lg:col-span-4">
            <OverviewCardSkeleton />
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="h-full min-h-[340px] w-full">
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-36" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))}
          </CardContent>
        </Card>
        <Card className="h-full w-full">
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-28" />
          </CardHeader>
          <CardContent className="space-y-4 p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OverviewCardSkeleton() {
  return (
    <Card className="h-full min-h-[340px] w-full overflow-hidden">
      <CardHeader className="pb-3">
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function ClientGalleryLoadingState() {
  return (
    <div className={pageClassName}>
      <div className="mb-2 flex items-center justify-end">
        <Skeleton className="h-9 w-28" />
      </div>
      <Card className="mb-4 p-4">
        <div className="space-y-6">
          {Array.from({ length: 2 }).map((_, group) => (
            <div key={group} className="space-y-2">
              <Skeleton className="h-4 w-36" />
              <div className="flex gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className="aspect-square h-28 w-28 rounded-md"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function ClientTrainingLoadingState() {
  return (
    <div className="mx-auto mb-10 w-full max-w-screen-2xl md:w-[90%]">
      <div className="mb-4 border-b px-4 pb-2 lg:px-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-2">
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-16" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="size-9" />
          </div>
        </div>
      </div>
      <div className="space-y-4 px-4 lg:px-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-8 w-56" />
          </div>
          <Skeleton className="h-9 w-20" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          <Card className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-5 w-40" />
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </Card>
          <Card className="p-6">
            <div className="space-y-3">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export function ClientNutritionLoadingState() {
  return (
    <div className="mb-10 w-full">
      <div className="mx-auto flex w-full max-w-screen-2xl flex-row items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-8 w-56" />
        </div>
        <Skeleton className="h-9 w-24" />
      </div>
      <div className="mx-auto mt-4 w-full max-w-screen-2xl px-3">
        <div className="mb-4 flex flex-wrap justify-end gap-2">
          <Skeleton className="h-14 w-16" />
          <Skeleton className="h-14 w-16" />
          <Skeleton className="h-14 w-16" />
          <Skeleton className="h-14 w-16" />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-8 w-20" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-5/6" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ClientCheckinResponseLoadingState() {
  return (
    <div className={pageClassName}>
      <Card className="mb-10 p-4">
        <div className="mb-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="size-9" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-9 w-36" />
            <Skeleton className="hidden h-9 w-28 sm:block" />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function ClientCheckinCompareLoadingState() {
  return (
    <div className={pageClassName}>
      <div className="mb-10 p-4">
        <div className="mb-3 flex items-center gap-4">
          <Skeleton className="size-9" />
          <Skeleton className="h-6 w-36" />
        </div>
        <div className="flex min-w-full flex-row justify-center gap-5">
          {Array.from({ length: 2 }).map((_, col) => (
            <Card key={col} className="w-full max-w-md p-4">
              <Skeleton className="mb-4 h-10 w-full" />
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ClientSidebarDetailLoadingState() {
  return (
    <div className={`${pageClassName} mb-6`}>
      <div className="mb-4 lg:hidden">
        <Skeleton className="h-14 w-full" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="hidden h-[calc(100svh-5.25rem)] flex-col overflow-hidden p-4 lg:flex">
          <Skeleton className="mb-4 h-10 w-full" />
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </Card>
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );
}

export function ClientRoadmapLoadingState() {
  return (
    <div className={`${pageClassName} mb-10`}>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-24" />
        </div>
        <Skeleton className="h-9 w-32" />
      </div>
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-5 w-28" />
            </div>
            <Skeleton className="h-16 w-full" />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ClientVaultLoadingState() {
  return (
    <div className="h-full min-h-0 w-full px-4 py-4 md:mx-auto md:w-[90%] md:px-0 md:py-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-28" />
      </div>
      <Card className="p-4">
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    </div>
  );
}
